/*
 * <cart-recommendations> - Goons Up cart drawer cross-sell.
 * Shows ONE product at a time; window height adapts to the current slide.
 * Per-option custom dropdowns; selecting a value resolves the matching available
 * variant, updates labels, the add-id, AND swaps the card image to that variant.
 * Rec-sourced adds are stamped with a hidden _source line-item property for attribution.
 */
if (!customElements.get('cart-recommendations')) {
  class CartRecommendations extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('[data-recs-track]');
      this.windowEl = this.querySelector('.cart-recs__window');
      this.slides = this.track ? Array.prototype.slice.call(this.track.querySelectorAll('[data-recs-slide]')) : [];
      if (!this.slides.length) {
        this.hidden = true;
        return;
      }
      this.hidden = false;
      this.index = 0;
      this.prevBtn = this.querySelector('[data-recs-prev]');
      this.nextBtn = this.querySelector('[data-recs-next]');
      this.addBtn = this.querySelector('[data-recs-add]');
      this.optlist = this.querySelector('[data-recs-optlist]');
      this.activeSlide = null;
      this.activeBtn = null;
      this.activeOptIndex = null;

      if (this.track) this.track.style.alignItems = 'flex-start';
      this.slides.forEach((s) => { s.style.height = 'auto'; });
      if (this.windowEl) this.windowEl.style.transition = 'height 0.25s ease';

      if (this.prevBtn) this.prevBtn.addEventListener('click', () => this.move(-1));
      if (this.nextBtn) this.nextBtn.addEventListener('click', () => this.move(1));
      if (this.addBtn) this.addBtn.addEventListener('click', () => this.addCurrent());
      this.addEventListener('click', (e) => this.onClick(e));

      if (!window.__cartRecsDocClose) {
        window.__cartRecsDocClose = true;
        document.addEventListener('mousedown', (e) => {
          if (e.target.closest('[data-recs-optlist]') || e.target.closest('[data-recs-picker-btn]')) return;
          document.querySelectorAll('cart-recommendations').forEach((el) => {
            if (typeof el.closePicker === 'function') el.closePicker();
          });
        });
      }

      if (this.slides.length <= 1) {
        [this.prevBtn, this.nextBtn].forEach((b) => {
          if (b) b.style.display = 'none';
        });
      }

      this.show(0);
    }

    fitHeight() {
      if (this.windowEl && this.slides[this.index]) {
        this.windowEl.style.height = this.slides[this.index].offsetHeight + 'px';
      }
    }

    parseVariants(raw) {
      return (raw || '').split('~~').filter(Boolean).map((s) => {
        const p = s.split(';;');
        return { id: p[0], img: p[1] || '', opts: (p[2] || '').split('^^') };
      });
    }

    selectedValues(slide) {
      const sel = [];
      slide.querySelectorAll('[data-recs-picker-btn]').forEach((btn) => {
        const i = Number(btn.dataset.optIndex);
        const label = btn.querySelector('[data-recs-picker-label]');
        sel[i] = label ? label.textContent.trim() : null;
      });
      return sel;
    }

    setLabels(slide, opts) {
      slide.querySelectorAll('[data-recs-picker-btn]').forEach((btn) => {
        const i = Number(btn.dataset.optIndex);
        const label = btn.querySelector('[data-recs-picker-label]');
        if (label && opts[i] != null) label.textContent = opts[i];
      });
    }

    onClick(e) {
      const opt = e.target.closest('.cart-recs__opt');
      if (opt && this.optlist && this.optlist.contains(opt)) {
        if (this.activeSlide && this.activeOptIndex != null) {
          this.chooseValue(this.activeSlide, this.activeOptIndex, opt.dataset.value);
        }
        this.closePicker();
        return;
      }
      const pbtn = e.target.closest('[data-recs-picker-btn]');
      if (pbtn) {
        const slide = pbtn.closest('[data-recs-slide]');
        if (this.activeBtn === pbtn && this.optlist && !this.optlist.hidden) this.closePicker();
        else this.openPicker(slide, pbtn);
      }
    }

    openPicker(slide, btn) {
      if (!this.optlist || !slide) return;
      const oi = Number(btn.dataset.optIndex);
      const variants = this.parseVariants(slide.dataset.recsVariants);
      const sel = this.selectedValues(slide);
      const seen = [];
      variants.forEach((v) => {
        const val = v.opts[oi];
        if (val != null && seen.indexOf(val) === -1) seen.push(val);
      });
      this.optlist.innerHTML = '';
      seen.forEach((val) => {
        const li = document.createElement('li');
        li.className = 'cart-recs__opt';
        li.setAttribute('role', 'option');
        li.dataset.value = val;
        if (val === sel[oi]) li.setAttribute('aria-selected', 'true');
        li.textContent = val;
        this.optlist.appendChild(li);
      });
      this.optlist.hidden = false;
      this.activeSlide = slide;
      this.activeBtn = btn;
      this.activeOptIndex = oi;
      btn.setAttribute('aria-expanded', 'true');
    }

    chooseValue(slide, oi, val) {
      const variants = this.parseVariants(slide.dataset.recsVariants);
      const sel = this.selectedValues(slide);
      sel[oi] = val;
      let v = variants.find((x) => x.opts.every((o, i) => o === sel[i]));
      if (!v) v = variants.find((x) => x.opts[oi] === val) || variants[0];
      if (!v) return;
      this.setLabels(slide, v.opts);
      slide.dataset.variantId = v.id;
      if (v.img) {
        const im = slide.querySelector('.cart-recs__img');
        if (im) im.src = v.img;
      }
    }

    closePicker() {
      if (this.optlist) this.optlist.hidden = true;
      if (this.activeBtn) this.activeBtn.setAttribute('aria-expanded', 'false');
      this.activeSlide = null;
      this.activeBtn = null;
      this.activeOptIndex = null;
    }

    show(i) {
      this.index = i;
      if (this.track) this.track.style.transform = 'translateX(' + -100 * i + '%)';
      this.fitHeight();
    }

    move(dir) {
      this.closePicker();
      const n = this.slides.length;
      if (!n) return;
      this.show((this.index + dir + n) % n);
    }

    addCurrent() {
      const slide = this.slides[this.index];
      if (!slide) return;
      this.addVariant(slide.dataset.variantId, this.addBtn);
    }

    addVariant(variantId, btn) {
      if (!variantId || (btn && btn.classList.contains('loading'))) return;
      if (btn) {
        btn.classList.add('loading');
        btn.setAttribute('disabled', '');
      }
      const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
      fetch(root + 'cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          items: [{ id: Number(variantId), quantity: 1, properties: { _source: 'cart_recs' } }],
          sections: ['cart-drawer', 'cart-icon-bubble'],
          sections_url: window.location.pathname,
        }),
      })
        .then((r) => r.json())
        .then((state) => {
          if (state && state.status) {
            if (btn) {
              btn.classList.remove('loading');
              btn.removeAttribute('disabled');
            }
            return;
          }
          const drawer = document.querySelector('cart-drawer');
          if (drawer && typeof drawer.renderContents === 'function') drawer.renderContents(state);
          document.dispatchEvent(new CustomEvent('cart:updated', { detail: { cart: state } }));
        })
        .catch(() => {
          if (btn) {
            btn.classList.remove('loading');
            btn.removeAttribute('disabled');
          }
        });
    }
  }
  customElements.define('cart-recommendations', CartRecommendations);
}
